import { xxhash64 } from './xxhash';
import type { KokoroGender } from './types';

/**
 * The 26 Kokoro-82M base voices, in canonical index order as defined by the
 * desktop ZeroVoice reference. Stage 7 resolves a single voice per agent;
 * Stage 8 will blend two of these via slerp with arc-property warping.
 */
export const KOKORO_VOICES = [
  // 0–9: American female (af_*)
  'af_alloy',
  'af_aoede',
  'af_bella',
  'af_jessica',
  'af_kore',
  'af_nicole',
  'af_nova',
  'af_river',
  'af_sarah',
  'af_sky',
  // 10–17: American male (am_*)
  'am_adam',
  'am_echo',
  'am_eric',
  'am_fenrir',
  'am_liam',
  'am_michael',
  'am_onyx',
  'am_puck',
  // 18–21: British female (bf_*)
  'bf_alice',
  'bf_emma',
  'bf_isabella',
  'bf_lily',
  // 22–25: British male (bm_*)
  'bm_daniel',
  'bm_fable',
  'bm_george',
  'bm_lewis',
] as const;

export type KokoroVoiceId = (typeof KOKORO_VOICES)[number];

export const FEMALE_IDX: readonly number[] = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 18, 19, 20, 21,
];
export const MALE_IDX: readonly number[] = [
  10, 11, 12, 13, 14, 15, 16, 17, 22, 23, 24, 25,
];

/** Salts reserved from the desktop reference. Stage 7 uses SALT_VOICE_A only;
 * the rest are placeholders so Stage 8's blend engine stays compatible. */
export const SALT_VOICE_A = 0x0001n;
export const SALT_VOICE_B = 0x0002n;
export const SALT_SLERP_T = 0x0003n;
export const SALT_PITCH = 0x0004n;
export const SALT_ENERGY = 0x0005n;

export interface ResolveInput {
  name: string;
  gender: KokoroGender;
  worldSeed: bigint;
}

export function genderPool(gender: KokoroGender): readonly number[] {
  if (gender === 'female') return FEMALE_IDX;
  if (gender === 'male') return MALE_IDX;
  return KOKORO_VOICES.map((_, i) => i);
}

/**
 * Deterministically resolve an agent name + gender + world seed to one of
 * the 26 Kokoro base voices. Same inputs always produce the same output.
 *
 * Requires `ensureXxhash()` to have resolved before calling.
 */
export function resolveKokoroVoice(input: ResolveInput): KokoroVoiceId {
  const pool = genderPool(input.gender);
  const seed = input.worldSeed ^ SALT_VOICE_A;
  const nameBytes = new TextEncoder().encode(input.name.trim().toLowerCase());
  const h = xxhash64(nameBytes, seed);
  const idx = pool[Number(h % BigInt(pool.length))];
  return KOKORO_VOICES[idx];
}

export function isValidKokoroVoiceId(v: string): v is KokoroVoiceId {
  return (KOKORO_VOICES as readonly string[]).includes(v);
}
